import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import CustomToast from "../../components/misc/CustomToast";
import Spinner from "../../components/misc/Spinner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/misc/Select";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import CaptionViolationMetricCards from "./CaptionViolationMetricCards";
import CaptionViolationsList from "./CaptionViolationsList";
import { getCaptionViolationMetrics, getCaptionViolations } from "./actions";
import {
    CaptionViolationMetrics,
    CaptionViolationRender,
    REASON_LABELS,
    SlideFailureReason,
} from "./types";

const PAGE_SIZE = 10;
const WINDOWS = [7, 30, 90];
const REASONS = Object.keys(REASON_LABELS) as SlideFailureReason[];

/**
 * MB-1314 (O2.6) — slideshow captions that broke a rule after the retry budget.
 *
 * Under the never-ship policy the auto flow no longer ships these: the slide is dropped
 * or the whole video is cancelled. The point of this page is that the drop/cancel rate
 * stays VISIBLE — a rising rate means model drift or a new listing pattern, and we should
 * see it here rather than hear about it from an agent. (Older rows marked "Shipped"
 * predate the policy.)
 */
const CaptionViolations: React.FC = () => {
    const isMobile = useIsMobile(900);

    const [metrics, setMetrics] = useState<CaptionViolationMetrics | null>(null);
    const [renders, setRenders] = useState<CaptionViolationRender[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [days, setDays] = useState(30);
    const [reason, setReason] = useState<SlideFailureReason | "all">("all");
    const [loadingList, setLoadingList] = useState(true);
    const [loadingMetrics, setLoadingMetrics] = useState(true);

    const fail = useCallback((message: string) => {
        toast.error(<CustomToast message={message} />);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoadingMetrics(true);
        getCaptionViolationMetrics(days)
            .then((m) => {
                if (!cancelled) setMetrics(m);
            })
            .catch(() => fail("Could not load caption violation metrics"))
            .finally(() => {
                if (!cancelled) setLoadingMetrics(false);
            });
        return () => {
            cancelled = true;
        };
    }, [days, fail]);

    useEffect(() => {
        let cancelled = false;
        setLoadingList(true);
        getCaptionViolations({
            page,
            limit: PAGE_SIZE,
            days,
            reason: reason === "all" ? undefined : reason,
        })
            .then((res) => {
                if (cancelled) return;
                setRenders(res.renders);
                setTotal(res.total);
            })
            .catch(() => fail("Could not load caption violations"))
            .finally(() => {
                if (!cancelled) setLoadingList(false);
            });
        return () => {
            cancelled = true;
        };
    }, [page, days, reason, fail]);

    return (
        <div
            className={cn(
                "flex h-[calc(100vh-64px)] flex-col gap-4 overflow-y-auto",
                isMobile ? "p-4" : "p-6"
            )}
        >
            <div
                className={cn(
                    "flex gap-4",
                    isMobile
                        ? "flex-col items-start"
                        : "flex-row items-center justify-between"
                )}
            >
                <div>
                    <h1 className="text-xl font-bold text-primary">
                        Caption violations
                    </h1>
                    <p className="text-xs text-gray-500">
                        Slideshow captions that still broke a rule after the retry
                        budget. In the auto flow they are dropped or the video is
                        cancelled — never shipped.
                    </p>
                </div>

                <div className="flex flex-row gap-2">
                    <Select
                        value={String(days)}
                        onValueChange={(v) => {
                            setDays(Number(v));
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className="w-[150px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {WINDOWS.map((d) => (
                                <SelectItem key={d} value={String(d)}>
                                    Last {d} days
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={reason}
                        onValueChange={(v) => {
                            setReason(v as SlideFailureReason | "all");
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="All rules" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All rules</SelectItem>
                            {REASONS.map((r) => (
                                <SelectItem key={r} value={r}>
                                    {REASON_LABELS[r]}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loadingMetrics || !metrics ? (
                <div className="flex justify-center py-10">
                    <Spinner />
                </div>
            ) : (
                <CaptionViolationMetricCards metrics={metrics} />
            )}

            <CaptionViolationsList
                renders={renders}
                total={total}
                page={page}
                limit={PAGE_SIZE}
                loading={loadingList}
                onPageChange={setPage}
            />
        </div>
    );
};

export default CaptionViolations;
